import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    TextField,
    Typography,
    Box,
    InputAdornment,
    CircularProgress,
    IconButton,
    Card,
    CardActionArea,
    Stack
} from '@mui/material';
import { Search as SearchIcon, Close as CloseIcon, Assignment as AssignmentIcon } from '@mui/icons-material';
import { initializeDB } from '../../config/db';
import { TOOLBOXTALK_STORE } from '../../config/constants';

const TopicSelectionModal = ({ open, onClose, onSelect }) => {
    const [topics, setTopics] = useState([]);
    const [filteredTopics, setFilteredTopics] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (open) {
            loadTopics();
            setSearchTerm('');
        }
    }, [open]);

    useEffect(() => {
        // Filter topics based on search term
        if (!searchTerm) {
            setFilteredTopics(topics);
        } else {
            const search = searchTerm.toLowerCase();
            const filtered = topics.filter(topic =>
                topic.title?.toLowerCase().includes(search) ||
                topic.externalId?.toString().includes(search)
            );
            setFilteredTopics(filtered);
        }
    }, [searchTerm, topics]);

    const loadTopics = async () => {
        setLoading(true);
        try {
            const db = await initializeDB();
            const tx = db.transaction(TOOLBOXTALK_STORE, 'readonly');
            const store = tx.objectStore(TOOLBOXTALK_STORE);
            const allTopics = await store.getAll();
            await tx.done;

            // Filter active topics and sort by externalId DESC
            const activeTopics = allTopics
                .filter(topic => topic.status !== false)
                .sort((a, b) => (b.externalId || 0) - (a.externalId || 0));

            setTopics(activeTopics);
            setFilteredTopics(activeTopics);
        } catch (error) {
            console.error('Error loading topics:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSelect = (topic) => {
        onSelect(topic);
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: { height: '80vh', maxHeight: '600px' }
            }}
        >
            <DialogTitle sx={{ pb: 1 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">Select Topic</Typography>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                {/* Search Field */}
                <Box sx={{ p: 2, pb: 1 }}>
                    <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Search topics by title or ID..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                        size="small"
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        {filteredTopics.length} topic{filteredTopics.length !== 1 ? 's' : ''} available
                    </Typography>
                </Box>

                {/* Topics List */}
                <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                            <CircularProgress />
                        </Box>
                    ) : filteredTopics.length === 0 ? (
                        <Box sx={{ p: 4, textAlign: 'center' }}>
                            <AssignmentIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                            <Typography variant="body2" color="text.secondary">
                                {searchTerm ? 'No topics found matching your search' : 'No topics available'}
                            </Typography>
                        </Box>
                    ) : (
                        <Stack spacing={2}>
                            {filteredTopics.map((topic) => (
                                <Card
                                    key={topic.id}
                                    elevation={0}
                                    sx={{
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 2,
                                        transition: 'all 0.2s',
                                        '&:hover': {
                                            boxShadow: 2,
                                            borderColor: 'primary.main',
                                            transform: 'translateY(-2px)'
                                        }
                                    }}
                                >
                                    <CardActionArea
                                        onClick={() => handleSelect(topic)}
                                        sx={{ p: 2.5 }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Box
                                                sx={{
                                                    width: 40,
                                                    height: 40,
                                                    borderRadius: 1,
                                                    backgroundColor: 'primary.light',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0
                                                }}
                                            >
                                                <AssignmentIcon sx={{ color: 'primary.main' }} />
                                            </Box>
                                            <Typography variant="body1" fontWeight={500} sx={{ flex: 1 }}>
                                                {topic.title || 'Untitled'}
                                            </Typography>
                                        </Box>
                                    </CardActionArea>
                                </Card>
                            ))}
                        </Stack>
                    )}
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default TopicSelectionModal;
